import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAskFrankSuggestions } from '../../services/api';

const DEBOUNCE_MS = 260;
const MIN_LENGTH = 2;
const MAX_SHOWN = 10;
const CACHE_MAX = 120;

const cache = new Map();

const remember = (key, value) => {
    cache.set(key, value);
    while (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
    return value;
};

export function useQuerySuggestions(value, { enabled = true } = {}) {
    const [suggestions, setSuggestions] = useState([]);
    const [highlight, setHighlight] = useState(-1);
    const timerRef = useRef(null);
    const latestRef = useRef(0);
    const valueRef = useRef('');
    const dismissedRef = useRef(null);

    useEffect(() => {
        const cleaned = String(value || '').trim().toLowerCase();
        valueRef.current = cleaned;

        clearTimeout(timerRef.current);
        setHighlight(-1);

        if (!enabled || cleaned.length < MIN_LENGTH) {
            setSuggestions([]);
            return undefined;
        }

        // Running a search closes the list, but picking a suggestion or landing
        // on the results page writes the same text straight back into the input.
        // Without remembering what was dismissed, that re-opened the list over
        // the results and left it there.
        if (dismissedRef.current === cleaned) {
            setSuggestions([]);
            return undefined;
        }

        dismissedRef.current = null;

        const cached = cache.get(cleaned);
        if (cached) {
            setSuggestions(cached);
            return undefined;
        }

        const prefix = [...cache.keys()]
            .filter(key => cleaned.startsWith(key) && cache.get(key).length === 0)
            .sort((a, b) => b.length - a.length)[0];

        if (prefix) {
            setSuggestions([]);
            return undefined;
        }

        const ticket = latestRef.current + 1;
        latestRef.current = ticket;

        timerRef.current = setTimeout(() => {
            getAskFrankSuggestions(cleaned)
                .then(res => {
                    if (latestRef.current !== ticket) return;
                    const rows = res.success ? (res.suggestions || []).slice(0, MAX_SHOWN) : [];
                    remember(cleaned, rows);
                    setSuggestions(rows);
                })
                .catch(() => {
                    if (latestRef.current === ticket) setSuggestions([]);
                });
        }, DEBOUNCE_MS);

        return () => clearTimeout(timerRef.current);
    }, [value, enabled]);

    const clear = useCallback((picked) => {
        clearTimeout(timerRef.current);
        latestRef.current += 1;
        dismissedRef.current = typeof picked === 'string'
            ? picked.trim().toLowerCase()
            : valueRef.current;
        setSuggestions([]);
        setHighlight(-1);
    }, []);

    const onKeyDown = useCallback((event, onPick) => {
        if (suggestions.length === 0) return false;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlight(current => (current + 1) % suggestions.length);
            return true;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlight(current => (current <= 0 ? suggestions.length - 1 : current - 1));
            return true;
        }

        if (event.key === 'Enter' && highlight >= 0) {
            event.preventDefault();
            const chosen = suggestions[highlight];
            onPick(chosen);
            clear(chosen);
            return true;
        }

        if (event.key === 'Escape') {
            clear();
            return true;
        }

        return false;
    }, [suggestions, highlight, clear]);

    return { suggestions, highlight, setHighlight, clear, onKeyDown };
}

export function SuggestionList({ suggestions, highlight, onPick, onHighlight }) {
    if (!suggestions || suggestions.length === 0) return null;

    return (
        <ul className="absolute left-0 right-0 top-full z-30 max-h-[320px] overflow-y-auto border border-gray-400 bg-white shadow-[0_4px_0_0_rgba(0,0,0,0.3)]">
            {suggestions.map((item, index) => (
                <li key={item}>
                    <button
                        type="button"
                        onMouseDown={(event) => { event.preventDefault(); onPick(item); }}
                        onMouseEnter={() => onHighlight(index)}
                        className={`block w-full truncate px-3 py-2 text-left text-[14px] ${
                            index === highlight ? 'bg-maroon text-white' : 'text-gray-800 hover:bg-gray-100'
                        }`}
                    >
                        {item}
                    </button>
                </li>
            ))}
        </ul>
    );
}
